import { motion } from 'framer-motion'
import { Copy } from 'lucide-react'

import { type GeneratedImage2 } from '#app/routes/resources+/generated-images.js'
import { decodeImageFormat } from '#app/utils/helpers.js'
import { useMediaQuery } from '#app/utils/hooks/useMediaQuery.js'
import { cn, getUserImgSrc } from '#app/utils/misc.js'
import {
	type ClientMessage,
	MESSAGE_ROLES,
} from '#app/utils/prompt-template.js'
import { CustomImage } from './custom-image'
import { Button } from './ui/button'
import { CircleCheckbox } from './ui/circle-checkbox'
import { Icon } from './ui/icon'

interface ChatMessageProps {
	message: ClientMessage
	chatId: string
	userImage: string
	showImage: boolean
	image: GeneratedImage2
	onDelete: () => void
	onRegenerate: () => void
	isLastMessage: boolean
	setEditDialogOpen: (open: boolean) => void
	setDialogMessage: (message: ClientMessage) => void
	onDeleteStack: () => void
	setEnableSelection: (enableSelection: boolean) => void
	enableSelection: boolean
	checked: boolean
	isLoading: boolean
}

interface MessageActionsProps {
	message: ClientMessage
	isLoading: boolean
	onDelete: () => void
	onDeleteStack: () => void
	setEditDialogOpen: (open: boolean) => void
	setDialogMessage: (message: ClientMessage) => void
}

function MessageActions({
	message,
	isLoading,
	onDelete,
	onDeleteStack,
	setEditDialogOpen,
	setDialogMessage,
}: MessageActionsProps) {
	return (
		<div
			className={cn(
				'items-center justify-center gap-1',
				message.content.replace(/\*/g, '').length <= 10
					? 'flex' // Always use flex for short messages
					: 'grid grid-cols-2 sm:flex', // Original responsive behavior
			)}
		>
			<Button
				variant="ghost"
				size="icon"
				className="h-6 w-6 justify-self-center md:h-8 md:w-8"
				title="Copy to clipboard"
				onClick={() => navigator.clipboard.writeText(message.content)}
				disabled={isLoading}
			>
				<Copy className="h-3 w-3" />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				className="h-6 w-6 justify-self-center md:h-8 md:w-8"
				title="Edit message"
				onClick={() => {
					setEditDialogOpen(true)
					setDialogMessage(message)
				}}
				disabled={isLoading}
			>
				<Icon name="pencil-1" className="h-3 w-3" />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				className="h-6 w-6 justify-self-center md:h-8 md:w-8"
				title="Delete message"
				onClick={onDelete}
				disabled={isLoading}
			>
				<Icon name="trash" className="h-3 w-3" />
			</Button>
			<Button
				variant="ghost"
				size="icon"
				className="h-6 w-6 justify-self-center md:h-8 md:w-8"
				onClick={onDeleteStack}
				title="Reset to this message"
				disabled={isLoading}
			>
				<Icon name="stack-minus" className="h-3 w-3" />
			</Button>
		</div>
	)
}

function MessageContent({
	message,
	isUser,
}: {
	message: ClientMessage
	isUser: boolean
}) {
	return (
		<div
			className={cn(
				'w-full rounded-2xl px-4 py-2',
				isUser
					? 'rounded-br-none bg-blue-600 text-white'
					: 'rounded-bl-none bg-gray-200 text-gray-800',
			)}
		>
			<p className="text-xs md:text-sm">
				{message.content.startsWith('*') && message.content.endsWith('*') ? (
					<em>{message.content.slice(1, -1)}</em>
				) : (
					message.content
				)}
			</p>
		</div>
	)
}

export function ChatMessage({
	message,
	userImage,
	showImage,
	image,
	onDelete,
	onRegenerate,
	isLastMessage,
	setEditDialogOpen,
	setDialogMessage,
	onDeleteStack,
	setEnableSelection,
	enableSelection,
	checked,
	isLoading,
}: ChatMessageProps) {
	const isUser = message.role === MESSAGE_ROLES.user
	const isLargeScreen = useMediaQuery('(min-width: 1280px)')

	// Calculate the x offset based on screen size
	const xOffset = isLargeScreen ? -25 : -15

	return (
		<article
			className={`flex w-full flex-col items-start justify-center py-1 ${isUser ? 'items-end' : 'items-start'}`}
		>
			<div
				className={cn(
					'group flex w-full items-end',
					isUser ? 'justify-end' : 'justify-start',
				)}
			>
				{isUser ? (
					<motion.div
						className="ml-3 mr-auto self-center"
						initial={{ x: -50, opacity: 0 }}
						animate={{
							x: enableSelection ? 0 : -50,
							opacity: enableSelection ? 1 : 0,
						}}
						transition={{ duration: 0.3 }}
					>
						<CircleCheckbox checked={checked} onChange={() => {}} />
					</motion.div>
				) : (
					<div className="flex items-center">
						<motion.div
							className="ml-3 mr-2"
							initial={{ x: -50, opacity: 0 }}
							animate={{
								x: enableSelection ? 0 : -50,
								opacity: enableSelection ? 1 : 0,
							}}
							transition={{ duration: 0.3 }}
						>
							<CircleCheckbox checked={checked} onChange={() => {}} />
						</motion.div>
						<motion.div
							className="flex-shrink-0"
							initial={{ x: -35 }}
							animate={{ x: enableSelection ? 0 : -35 }}
							transition={{ duration: 0.3 }}
						>
							{showImage && <AssistantImage image={image} />}
						</motion.div>
					</div>
				)}
				<motion.div
					className={`flex w-full flex-col items-center justify-center ${
						!isUser && !showImage ? 'ml-10' : ''
					}`}
					initial={{ x: !isUser ? xOffset : 0 }}
					animate={{
						x: enableSelection && !isUser ? 0 : !isUser ? xOffset : 0,
					}}
					transition={{ duration: 0.3 }}
				>
					<div
						className={`flex w-full flex-row items-center gap-2 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
					>
						<div className="flex flex-col items-end">
							<MessageContent message={message} isUser={isUser} />
						</div>
						<div
							className={cn(
								'flex-shrink-0 transition-opacity duration-200',
								enableSelection ? 'invisible' : 'invisible group-hover:visible',
							)}
							style={{ width: 'auto' }}
						>
							<MessageActions
								message={message}
								isLoading={isLoading}
								onDelete={onDelete}
								onDeleteStack={onDeleteStack}
								setEditDialogOpen={setEditDialogOpen}
								setDialogMessage={setDialogMessage}
							/>
						</div>
					</div>
				</motion.div>
				{isUser && (
					<div className="ml-2 hidden w-8 flex-shrink-0 md:block">
						{showImage && (
							<img
								src={getUserImgSrc(userImage)}
								alt="User avatar"
								className="h-8 w-8 rounded-full"
							/>
						)}
					</div>
				)}
			</div>

			{!isUser && isLastMessage && (
				<div className="flex flex-row items-center gap-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={onRegenerate}
						className="group ml-10 mt-0 self-start bg-opacity-50 p-0 text-xs italic text-muted-foreground ring-transparent ring-offset-transparent hover:bg-transparent"
					>
						Regenerate response
						<Icon
							name="sparkle"
							className="ml-1 h-4 w-4 transition-all duration-300 ease-in-out group-hover:rotate-[15deg]"
						/>
					</Button>
					<Button
						variant="ghost"
						size="sm"
						onClick={() => setEnableSelection(true)}
						className="group mt-0 self-start bg-opacity-50 p-0 text-xs italic text-muted-foreground ring-transparent ring-offset-transparent hover:bg-transparent"
					>
						Select multiple
						<Icon name="plus" className="ml-1 h-4 w-4" />
					</Button>
				</div>
			)}
		</article>
	)
}

const AssistantImage = ({ image }: { image: GeneratedImage2 }) => {
	return (
		<figure className="relative mr-2">
			<CustomImage
				image={image}
				width={32}
				actualImageUrl={image.url}
				imageFormat={decodeImageFormat(image.format)}
				className={`${!image.url ? 'relative' : ''} h-8 w-8 rounded-full border-1 border-gray-400`}
				breakpoints={[100]}
			/>
		</figure>
	)
}
